import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/*
 * Trial result codes — practice-configurable labels used by trial and
 * task_analysis data points. Server:
 *   [server/src/routes/trial_result_codes.js].
 *
 * Immutability contract (enforced server-side):
 *   - `code` and `isCorrect` are set once on create and ignored on PATCH.
 *   - DELETE is soft: the server stamps archivedAt and leaves the row so
 *     historical data_points referencing the code still resolve to a label.
 *   - An archived row can be brought back via POST /:id/restore; the server
 *     refuses to create a new row whose code matches any existing row
 *     (active or archived), so restore is the only way to "reuse" a code.
 */

export interface TrialResultCode {
    id: string;
    code: string;
    label: string;
    isCorrect: boolean;
    color: string | null;
    sortOrder: number;
    archivedAt: string | null;
}

export interface NewTrialResultCode {
    code: string;
    label: string;
    isCorrect: boolean;
    color?: string | null;
    sortOrder?: number;
}

export interface UpdateTrialResultCodePayload {
    label?: string;
    color?: string | null;
    sortOrder?: number;
}

export function useTrialResultCodes(includeArchived = false) {
    return useQuery({
        queryKey: ['trial-result-codes', includeArchived] as const,
        queryFn: async () => {
            const res = await api.get<TrialResultCode[]>('/trial-result-codes', {
                params: includeArchived ? { includeArchived: 'true' } : {},
            });
            return res.data;
        },
        staleTime: 60_000,
    });
}

export function useCreateTrialResultCode() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: NewTrialResultCode) => {
            const res = await api.post<TrialResultCode>('/trial-result-codes', payload);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['trial-result-codes'] }),
    });
}

export function useUpdateTrialResultCode() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: UpdateTrialResultCodePayload }) => {
            const res = await api.patch<TrialResultCode>(`/trial-result-codes/${id}`, payload);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['trial-result-codes'] }),
    });
}

export function useArchiveTrialResultCode() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const res = await api.delete<TrialResultCode>(`/trial-result-codes/${id}`);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['trial-result-codes'] }),
    });
}

export function useRestoreTrialResultCode() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const res = await api.post<TrialResultCode>(`/trial-result-codes/${id}/restore`);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['trial-result-codes'] }),
    });
}

/**
 * Reorder active (non-archived) result codes. The payload must list every
 * active code's id exactly once in the desired order; the server rewrites
 * each row's sort_order to its position in the array. Archived rows keep
 * their existing sort_order.
 *
 * Optimistic update: we rewrite the cache immediately so the drag-and-drop
 * feels instant. If the PUT fails, the `onError` snapshot roll-back restores
 * the prior order.
 */
export function useReorderTrialResultCodes() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (ids: string[]) => {
            const res = await api.put<TrialResultCode[]>('/trial-result-codes/reorder', { ids });
            return res.data;
        },
        onMutate: async (ids) => {
            await qc.cancelQueries({ queryKey: ['trial-result-codes'] });
            const snapshots = qc.getQueriesData<TrialResultCode[]>({ queryKey: ['trial-result-codes'] });
            for (const [key, rows] of snapshots) {
                if (!rows) continue;
                const byId = new Map(rows.map((r) => [r.id, r]));
                const reordered = ids
                    .map((id, idx) => {
                        const row = byId.get(id);
                        return row ? { ...row, sortOrder: idx + 1 } : null;
                    })
                    .filter((r): r is TrialResultCode => r !== null);
                // Preserve any archived rows (not in the id list) at the end.
                const archived = rows.filter((r) => !ids.includes(r.id));
                qc.setQueryData(key, [...reordered, ...archived]);
            }
            return { snapshots };
        },
        onError: (_err, _ids, ctx) => {
            if (!ctx) return;
            for (const [key, rows] of ctx.snapshots) {
                qc.setQueryData(key, rows);
            }
        },
        onSettled: () => qc.invalidateQueries({ queryKey: ['trial-result-codes'] }),
    });
}
